import React, { useEffect, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button, Layout, Spin } from 'antd'
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  HeartOutlined,
  HeartFilled,
  SearchOutlined,
  LeftOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import { useAppDispatch, useAppSelector } from '../../utils/redux/store'
import { attemptGetPlaylist, Song } from '../../utils/redux/playlistSlice'
import {
  attemptAddPlaylistToFavs,
  attemptDeletePlaylistFromFavs,
  attemptGetFavs,
} from '../../utils/redux/favsSlice'
import { useAudioPlayer } from '../../contexts/AudioPlayerContext'
import { apiBaseURL } from '../../utils/api'

import './Playlist.scss'

dayjs.extend(relativeTime)

const { Content } = Layout

export default function Playlist() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { i18n } = useTranslation('playlist')

  const playlist = useAppSelector((state) =>
    id ? state.playlist.playlists[id] : undefined,
  )

  const favPlaylists = useAppSelector((state) => state.favs.playlists)

  const moodsDict = useAppSelector(
    (s: any) => s.playlist?.moods ?? s.playlist?.categories?.moods ?? [],
  )
  const stylesDict = useAppSelector(
    (s: any) => s.playlist?.styles ?? s.playlist?.categories?.styles ?? [],
  )
  const daytimesDict = useAppSelector(
    (s: any) => s.playlist?.daytimes ?? s.playlist?.categories?.daytimes ?? [],
  )

  const { currentSong, isPlaying, isLoading, playSong, playQueue, togglePlayPause } =
    useAudioPlayer()

  useEffect(() => {
    if (id) dispatch(attemptGetPlaylist(id))
  }, [id, dispatch])

  useEffect(() => {
    dispatch(attemptGetFavs())
  }, [dispatch])

  const isFavorite = favPlaylists?.some((fav) => fav._id === id) || false

  const asText = useCallback(
    (val: any): string => {
      if (val == null) return ''
      if (typeof val === 'string' || typeof val === 'number') return String(val)
      if (Array.isArray(val)) return val.map(asText).filter(Boolean).join(' ')
      if (typeof val === 'object') {
        const lang = (i18n.language || 'en').split('-')[0]
        const pick = val?.[lang] ?? val?.en ?? val?.ru ?? val?.fr
        if (pick != null) return asText(pick)
        const firstStr = Object.values(val).find((v) => typeof v === 'string')
        return firstStr ? String(firstStr) : ''
      }
      return ''
    },
    [i18n.language],
  )

  const formatTotalDurationFigma = useCallback((seconds?: number) => {
    if (!seconds || seconds <= 0) return '0 minutes'
    const totalMinutes = Math.round(seconds / 60)

    if (totalMinutes < 60) {
      const m = Math.max(1, totalMinutes)
      return `${m} minute${m === 1 ? '' : 's'}`
    }

    const hours = Math.round(totalMinutes / 60)
    return `${hours} hour${hours === 1 ? '' : 's'}`
  }, [])

  const formatUpdatedAgoFigma = useCallback((date?: string | Date) => {
    if (!date) return ''
    const d = dayjs(date)
    const now = dayjs()

    const minutes = now.diff(d, 'minute')
    if (minutes < 60) {
      const m = Math.max(1, minutes)
      return `${m} minute${m === 1 ? '' : 's'} ago`
    }

    const hours = now.diff(d, 'hour')
    if (hours < 24) {
      const h = Math.max(1, hours)
      return `${h} hour${h === 1 ? '' : 's'} ago`
    }

    const days = now.diff(d, 'day')
    if (days < 30) {
      const dd = Math.max(1, days)
      return `${dd} day${dd === 1 ? '' : 's'} ago`
    }

    const months = now.diff(d, 'month')
    if (months < 12) {
      const mm = Math.max(1, months)
      return `${mm} month${mm === 1 ? '' : 's'} ago`
    }

    const years = now.diff(d, 'year')
    const yy = Math.max(1, years)
    return `${yy} year${yy === 1 ? '' : 's'} ago`
  }, [])

  const titleText = useMemo(() => asText(playlist?.title), [playlist?.title, asText])

  const descText = useMemo(
    () => asText((playlist as any)?.description),
    [(playlist as any)?.description, asText],
  )

  const coverUrl = useMemo(() => {
    if (!playlist?.image) return ''
    return `${apiBaseURL}/uploaded/playlists/${playlist.image}`
  }, [playlist?.image])

  const songsCount = playlist?.songsCount ?? playlist?.songs?.length ?? 0
  const totalDurationText = useMemo(() => {
    return formatTotalDurationFigma((playlist as any)?.totalDuration)
  }, [formatTotalDurationFigma, (playlist as any)?.totalDuration])

  const updatedAgoText = useMemo(() => {
    return formatUpdatedAgoFigma((playlist as any)?.updatedAt)
  }, [formatUpdatedAgoFigma, (playlist as any)?.updatedAt])

  const idToTitle = useMemo(() => {
    const map = new Map<string, string>()
    const put = (arr: any[]) => {
      ;(arr || []).forEach((it) => {
        const key = String(it?._id ?? '')
        const val = asText(it?.title ?? it?.name)
        if (key && val) map.set(key, val)
      })
    }
    put(moodsDict)
    put(stylesDict)
    put(daytimesDict)
    return map
  }, [moodsDict, stylesDict, daytimesDict, asText])

  const isMongoId = (s: string) => /^[a-f\d]{24}$/i.test(s)

  const chips: string[] = useMemo(() => {
    const p: any = playlist || {}

    const sources = [
      ...(Array.isArray(p.moods) ? p.moods : []),
      ...(Array.isArray(p.styles) ? p.styles : []),
      ...(Array.isArray(p.daytimes) ? p.daytimes : []),
      ...(Array.isArray(p.tags) ? p.tags : p.tags ? [p.tags] : []),
      ...(Array.isArray(p.genres) ? p.genres : p.genres ? [p.genres] : []),
      ...(Array.isArray(p.categories)
        ? p.categories
        : p.categories
          ? [p.categories]
          : []),
    ]

    const out: string[] = []
    const seen = new Set<string>()

    for (const v of sources) {
      let label = ''

      if (typeof v === 'object') {
        label = asText(v?.title ?? v?.name ?? v)
      } else {
        const s = String(v)
        label = idToTitle.get(s) ?? s
        if (isMongoId(label) && !idToTitle.get(s)) label = ''
      }

      label = label.trim()
      if (!label) continue

      const key = label.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)

      out.push(label)
      if (out.length >= 6) break
    }

    return out
  }, [playlist, idToTitle, asText])

  // ============ время трека (оставляем мм:сс) ============
  const formatDurationSong = (seconds?: number) => {
    if (!seconds) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getSongImage = (song: any) =>
    song?.imageUrl ||
    (song?.image ? `${apiBaseURL}/uploaded/songs/${song.image}` : '') ||
    ''

  // ============ play logic ============
  const handlePlaySong = (song: Song) => {
    if (!playlist?.songs?.length) return

    if (currentSong?._id === song._id) {
      togglePlayPause()
      return
    }

    playSong(song, playlist.songs)
  }

  const handlePlayAll = () => {
    if (!playlist?.songs?.length) return
    playQueue(playlist.songs, 0)
  }

  const handleToggleFavorite = async () => {
    if (!id) return

    try {
      if (isFavorite) await dispatch(attemptDeletePlaylistFromFavs(id)).unwrap()
      else await dispatch(attemptAddPlaylistToFavs(id)).unwrap()

      dispatch(attemptGetFavs())
    } catch (e) {}
  }

  if (!playlist) {
    return (
      <div className="pl-loading">
        <Spin size="large" />
      </div>
    )
  }

  return (
    <Content
      className="pl"
      style={
        {
          ['--pl-bg' as any]: coverUrl ? `url(${coverUrl})` : 'none',
        } as React.CSSProperties
      }
    >
      {/* TOP BAR (DESKTOP) */}
      <div className="pl-top">
        <div className="pl-search">
          <SearchOutlined className="pl-search__icon" />
          <input
            className="pl-search__input"
            placeholder="Search songs, albums, artists, podcasts"
          />
        </div>
      </div>

      <div className="pl-wrap">
        {/* LEFT */}
        <aside className="pl-left">
          <div className="pl-card">
            <div className="pl-coverWrap">
              <button
                type="button"
                className="pl-back"
                onClick={() => navigate(-1)}
                aria-label="back"
              >
                <LeftOutlined />
              </button>

              <img className="pl-cover" src={coverUrl} alt={titleText} />

              <button
                type="button"
                className="pl-fav"
                onClick={handleToggleFavorite}
                aria-label="favorite"
                title="Favorite"
              >
                {isFavorite ? <HeartFilled /> : <HeartOutlined />}
              </button>
            </div>

            <div className="pl-title">{titleText}</div>

            <div className="pl-meta">
              <span>{songsCount} songs</span>
              <span className="pl-dot">|</span>
              <span>{totalDurationText}</span>
              <span className="pl-dot">|</span>
              <span>Updated {updatedAgoText || ''}</span>
            </div>

            {/*  описание на текущем языке */}
            {descText && <div className="pl-desc">{descText}</div>}

            {chips.length > 0 && (
              <div className="pl-chips">
                {chips.map((c, idx) => (
                  <span
                    key={`${c}-${idx}`}
                    className={`pl-chip ${idx === 0 ? 'is-primary' : ''}`}
                  >
                    {c}
                  </span>
                ))}
              </div>
            )}

            {/* если нужно вернуть playAll — просто раскомментируй */}
            {/* <div className="pl-actions">
              <Button
                className="pl-playAll"
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handlePlayAll}
              >
                Play all
              </Button>
            </div> */}
          </div>
        </aside>

        {/* RIGHT */}
        <section className="pl-right">
          <div className="pl-list">
            {playlist.songs?.map((song: any) => {
              const isCurrent = currentSong?._id === song._id
              const isCurrentPlaying = isCurrent && isPlaying
              const img = getSongImage(song)

              return (
                <div
                  key={song._id}
                  className={`pl-row ${isCurrent ? 'is-current' : ''}`}
                  onClick={() => handlePlaySong(song)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="pl-row__left">
                    <div className="pl-row__imgWrap">
                      {img ? (
                        <img className="pl-row__img" src={img} alt={song.title} />
                      ) : (
                        <div className="pl-row__img pl-row__img--empty">
                          <CustomerServiceOutlined />
                        </div>
                      )}

                      <div className="pl-row__overlay">
                        <Button
                          type="text"
                          className="pl-row__playBtn"
                          icon={
                            isCurrentPlaying ? (
                              <PauseCircleOutlined />
                            ) : (
                              <PlayCircleOutlined />
                            )
                          }
                          onClick={(e) => {
                            e.stopPropagation()
                            handlePlaySong(song)
                          }}
                          loading={isLoading && isCurrent}
                        />
                      </div>
                    </div>

                    <div className="pl-row__text">
                      <div className="pl-row__title">{song.title}</div>
                      <div className="pl-row__sub">{song.artist}</div>
                    </div>
                  </div>

                  <div className="pl-row__right">
                    <div className="pl-row__time">
                      {formatDurationSong(song.duration)}
                    </div>

                    <button
                      type="button"
                      className="pl-row__fav"
                      aria-label="like"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <HeartOutlined />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      </div>
    </Content>
  )
}
